import { useMemo, useState, type ReactElement } from 'react';
import { Plus, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import { Skeleton } from '@/components/ui/skeleton';
import { PageHeader } from '@/components/shell/PageHeader';

import {
  useAuthProviderMutations,
  useAuthProviders,
  useOidcCredentials,
  type AuthProviderInput,
  type AuthProviderViewModel,
  type OidcCredentialViewModel,
} from '@/features/admin-auth/api';
import { AuthProviderCreateDialog } from '@/features/admin-auth/components/AuthProviderCreateDialog';
import { AuthProvidersTable } from '@/features/admin-auth/components/AuthProvidersTable';
import { DeleteAuthConfirmDialog } from '@/features/admin-auth/components/DeleteAuthConfirmDialog';

export function AdminAuthProvidersPage(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const query = useAuthProviders();
  const credentialsQuery = useOidcCredentials();
  const { create, remove } = useAuthProviderMutations();

  const [createOpen, setCreateOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState<AuthProviderViewModel | null>(null);

  const rows = useMemo(
    () => (query.isSuccess ? [...query.data].sort((a, b) => a.host.localeCompare(b.host)) : []),
    [query.isSuccess, query.data],
  );

  const credentials = useMemo<readonly OidcCredentialViewModel[]>(
    () => credentialsQuery.data ?? [],
    [credentialsQuery.data],
  );

  const credentialsById = useMemo(
    () => new Map(credentials.map((c) => [c.id, c])),
    [credentials],
  );

  function openDetail(id: string): void {
    void navigate(`/platform/auth-providers/${encodeURIComponent(id)}`);
  }

  function handleCreate(input: AuthProviderInput): void {
    create.mutate(input, {
      onSuccess: (created) => {
        setCreateOpen(false);
        create.reset();
        openDetail(created.id);
      },
    });
  }

  return (
    <>
      <PageHeader
        icon={<ShieldCheck className="h-[18px] w-[18px]" aria-hidden="true" />}
        title={t('routes.admin.authProviders.heading')}
        subtitle={t('routes.admin.authProviders.subheading')}
      />
      <div className="px-8 py-5 space-y-8 bg-background">
        <section aria-labelledby="admin-auth-providers-heading" className="space-y-4">
          <header className="flex items-start justify-between gap-4">
            <div>
              <h2 id="admin-auth-providers-heading" className="text-base font-semibold text-foreground">
                {t('routes.admin.authProviders.listHeading')}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {t('routes.admin.authProviders.listSubheading')}
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => {
                create.reset();
                setCreateOpen(true);
              }}
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              {t('routes.admin.authProviders.addAction')}
            </Button>
          </header>

          {query.isPending && <RowsSkeleton />}

          {query.isError && (
            <div className="max-w-xl">
              <FetchErrorCallout
                error={query.error}
                heading={t('routes.admin.authProviders.error.heading')}
                message={t('routes.admin.authProviders.error.message')}
                codeLabel={t('routes.admin.authProviders.error.codeLabel')}
                onRetry={() => {
                  query.refetch().catch(() => undefined);
                }}
                retryLabel={t('routes.admin.authProviders.error.retry')}
              />
            </div>
          )}

          {query.isSuccess && rows.length === 0 && (
            <p className="text-sm text-muted-foreground" role="status">
              {t('routes.admin.authProviders.empty')}
            </p>
          )}

          {query.isSuccess && rows.length > 0 && (
            <DataTable aria-label={t('routes.admin.authProviders.heading')}>
              <AuthProvidersTable
                rows={rows}
                credentialsById={credentialsById}
                onEdit={(row) => {
                  openDetail(row.id);
                }}
                onDelete={(row) => {
                  remove.reset();
                  setDeleteRow(row);
                }}
                pendingDeleteId={remove.isPending && deleteRow !== null ? deleteRow.id : null}
              />
            </DataTable>
          )}
        </section>
      </div>

      <AuthProviderCreateDialog
        open={createOpen}
        credentials={credentials}
        onOpenChange={(next) => {
          if (!next && create.isPending) return;
          if (!next) {
            setCreateOpen(false);
            create.reset();
          } else {
            setCreateOpen(true);
          }
        }}
        onSubmit={handleCreate}
        pending={create.isPending}
        submitError={create.error}
      />

      <DeleteAuthConfirmDialog
        open={deleteRow !== null}
        title={t('routes.admin.authProviders.delete.title')}
        message={t('routes.admin.authProviders.delete.message', { host: deleteRow?.host ?? '' })}
        cancelLabel={t('routes.admin.authProviders.delete.cancel')}
        confirmLabel={t('routes.admin.authProviders.delete.confirm')}
        onCancel={() => {
          if (remove.isPending) return;
          setDeleteRow(null);
          remove.reset();
        }}
        onConfirm={() => {
          if (deleteRow === null) return;
          remove.mutate(deleteRow.id, {
            onSuccess: () => {
              setDeleteRow(null);
              remove.reset();
            },
          });
        }}
        pending={remove.isPending}
        submitError={remove.error}
        submitErrorLabel={(code) => t('routes.admin.authProviders.form.errors.submit', { code })}
      />
    </>
  );
}

function RowsSkeleton(): ReactElement {
  return (
    <div className="space-y-2" aria-hidden="true">
      {Array.from({ length: 3 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full rounded-md" />
      ))}
    </div>
  );
}
